import React,{ forwardRef, JSX, ReactNode } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    Paper,
    SxProps,
    Theme,
} from '@mui/material';

interface Column<T> {
    id: string;
    label: string;
    align?: 'left' | 'center' | 'right';
    width?: string | number;
    render?: (row: T, index: number) => ReactNode;
}

interface DataTableProps<T> {
    columns: Column<T>[];
    data: T[];
    footer?: ReactNode;
    striped?: boolean;
    stickyHeader?: boolean;
    maxHeight?: string | number;
    sx?: SxProps<Theme>;
    onRowClick?: (row: T, index: number) => void;
}

function DataTableInner<T extends Record<string, unknown>>(
    {
        columns,
        data,
        footer,
        striped = true,
        stickyHeader = false,
        maxHeight,
        sx,
        onRowClick,
    }: DataTableProps<T>,
    ref: React.Ref<HTMLDivElement>
) {
    return (
        <TableContainer
            ref={ref}
            component={Paper}
            sx={{
                maxHeight,
                ...sx,
            }}
        >
            <Table stickyHeader={stickyHeader} size="small">
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell
                                key={column.id}
                                align={column.align ?? 'left'}
                                sx={{
                                    width: column.width,
                                    fontWeight: 600,
                                }}
                            >
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row, rowIndex) => (
                        <TableRow
                            key={rowIndex}
                            hover
                            onClick={() => onRowClick?.(row, rowIndex)}
                            sx={{
                                cursor: onRowClick ? 'pointer' : 'default',
                                bgcolor: striped && rowIndex % 2 === 0 ? 'action.hover' : undefined,
                            }}
                        >
                            {columns.map((column) => (
                                <TableCell key={column.id} align={column.align ?? 'left'}>
                                    {column.render
                                        ? column.render(row, rowIndex)
                                        : String(row[column.id] ?? '')}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
                {footer && <TableFooter>{footer}</TableFooter>}
            </Table>
        </TableContainer>
    );
}

export const DataTable = forwardRef(DataTableInner) as <T extends Record<string, unknown>>(
    props: DataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => JSX.Element;
